import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { Cell, DataRow, HeaderRow, InlineDataTable, TableHeader } from 'uiComponents/table'
import { TooltipWrapper } from 'uiComponents/tooltip.new/tooltipWrapper'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { MessageKind } from 'uiComponents/messages'
import { delay } from 'utils'
import { CapacityMemberCounts, VISITOR_KINDS, VisitorCapacities, VisitorKind } from 'capacity/api/capacityGateway'
import { useCreateVisitorCapacityMutation } from 'capacity/reduxQuery'
import { capacityPaths } from 'capacity/paths'
import { LIST } from 'capacity/copy'

const COLUMN_WIDTHS = [null, '9rem', '9rem']
const CELL_CLASS = 'capacity-list-cell wrapContent overflow-allowed'
const MESSAGE_VISIBLE_MS = 3000

interface VisitorsSectionProps {
    accountSlug: string
    visitors: VisitorCapacities | undefined
    failed: boolean
    counts: CapacityMemberCounts | undefined
    countsFailed: boolean
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    hideMessage: (id: string) => void
}

interface VisitorRowProps extends VisitorsSectionProps {
    kind: VisitorKind
}

function VisitorRow({
    accountSlug,
    kind,
    visitors,
    failed,
    counts,
    countsFailed,
    replaceMessages,
    hideMessage,
}: VisitorRowProps) {
    const history = useHistory()
    const [create, state] = useCreateVisitorCapacityMutation()
    const copy = LIST.visitorRows[kind]
    const capacity = visitors ? visitors[kind] : undefined
    const href = capacity ? capacityPaths.item(accountSlug, capacity.id) : null
    const count = capacity && counts ? counts.counts[capacity.id] : undefined
    const productsCount = count ? count.products + count.optionItems : capacity && counts ? 0 : null

    const setUp = async () => {
        const result = await create({ accountSlug, kind })
        const setUpFailed = 'error' in result
        const id = setUpFailed ? 'server_error' : 'success'
        replaceMessages(
            id,
            setUpFailed ? 'error' : 'success',
            setUpFailed ? LIST.setUpFailed(copy.name) : LIST.setUpDone(copy.name),
        )
        await delay(MESSAGE_VISIBLE_MS)
        hideMessage(id)
    }

    const productsCell = () => {
        if (visitors === undefined) {
            return failed ? (
                <span className="capacity-list-not-loaded">{LIST.notLoaded}</span>
            ) : (
                <SkeletonBar width="4.5rem" height="0.85rem" className="capacity-list-skeleton-bar" />
            )
        }
        if (capacity) {
            if (productsCount !== null) {
                return productsCount
            }
            return countsFailed ? (
                <span className="capacity-list-not-loaded">{LIST.notLoaded}</span>
            ) : (
                <SkeletonBar width="4.5rem" height="0.85rem" className="capacity-list-skeleton-bar" />
            )
        }
        return (
            <button
                type="button"
                className="capacity-list-create capacity-visitor-setup"
                aria-label={LIST.setUpLabel(copy.name)}
                disabled={state.isLoading}
                onClick={(event) => {
                    event.stopPropagation()
                    setUp()
                }}
            >
                {state.isLoading ? LIST.settingUp : LIST.setUp}
            </button>
        )
    }

    return (
        <DataRow
            role="row"
            className={classNames('capacity-list-row capacity-visitor-row', { 'is-link': !!href })}
            onClick={() => href && history.push(href)}
        >
            <Cell role="cell" className={classNames(CELL_CLASS, 'capacity-list-name-cell')}>
                <span className="capacity-visitor-name-stack">
                    {href ? (
                        <Link to={href} className="capacity-list-name" onClick={(event) => event.stopPropagation()}>
                            {copy.name}
                        </Link>
                    ) : (
                        <span className="capacity-list-name">{copy.name}</span>
                    )}
                    <span className="capacity-visitor-subtitle">{copy.subtitle}</span>
                </span>
            </Cell>
            <Cell role="cell" className={CELL_CLASS}>
                <TooltipWrapper
                    text={LIST.visitorCountsTooltip[kind]}
                    arrowPosition="right"
                    className="capacity-tip capacity-tip-right"
                    revealOnFocus
                >
                    <span tabIndex={0} className="capacity-list-counts" onClick={(event) => event.stopPropagation()}>
                        {copy.counts}
                    </span>
                </TooltipWrapper>
            </Cell>
            <Cell role="cell" className={CELL_CLASS}>
                {productsCell()}
            </Cell>
        </DataRow>
    )
}

export function VisitorsSection(props: VisitorsSectionProps) {
    return (
        <section className="capacity-list-section" aria-labelledby="visitors-heading">
            <div className="capacity-list-heading">
                <h2 id="visitors-heading" className="capacity-list-title">
                    {LIST.visitors}
                </h2>
                <p className="capacity-list-meaning">{LIST.visitorsMeaning}</p>
            </div>
            <div className="capacity-table-card capacity-list-card">
                <InlineDataTable bordered role="table" className="capacity-table" columnWidths={COLUMN_WIDTHS}>
                    <HeaderRow role="row" className="capacity-table-head">
                        <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                            {LIST.columns.name}
                        </TableHeader>
                        <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                            {LIST.columns.counts}
                        </TableHeader>
                        <TableHeader nonInteractive role="columnheader" className="capacity-table-th">
                            {LIST.columns.products}
                        </TableHeader>
                    </HeaderRow>
                    <div role="rowgroup">
                        {VISITOR_KINDS.map((kind) => (
                            <VisitorRow key={kind} kind={kind} {...props} />
                        ))}
                    </div>
                </InlineDataTable>
            </div>
        </section>
    )
}
